/**
 * Converts money.xlsx (a SheetJS workbook) into the app's JSON database.
 * Used by Settings → Import spreadsheet and by scripts/import-xlsx.mjs.
 *
 * Reads the EUR-era sheets (2025+):
 *   - daily rows (B date, C total assets, D loan, F note) → snapshots
 *   - the latest sheet's balance block (rows 3–6 × columns D–F) → accounts
 *   - the latest loan value and its daily growth factor (=D12*1.0002105) → loans
 *   - Törlesztő / Fizetés and the "Hónap napja" due days → recurring items
 *   - "Tervezett vételek" → planned purchases
 * Sheets before 2025 are in HUF and are skipped.
 */
export function convertWorkbook(XLSX, wb) {
  const years = wb.SheetNames.filter((n) => /^\d{4}$/.test(n) && Number(n) >= 2025).sort()
  if (!years.length) throw new Error('No 2025+ year sheets found')

  const cell = (ws, addr) => ws[addr]
  const val = (ws, addr) => { const c = cell(ws, addr); return c ? c.v : undefined }
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const key = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const data = { settings: { currency: 'EUR', locale: 'hu-HU' }, accounts: {}, snapshots: {}, loans: {}, recurring: {}, wishlist: {}, transactions: {} }

  // ---- Snapshots: row 11 is Jan 1 of the sheet's year (the 2026 sheet still carries 2025 dates in column B).
  let loanFactor = null
  let lastLoan = null
  for (const y of years) {
    const ws = wb.Sheets[y]
    const range = XLSX.utils.decode_range(ws['!ref'])
    for (let r = 11; r <= range.e.r + 1; r++) {
      const assets = num(val(ws, `C${r}`))
      const debt = num(val(ws, `D${r}`))
      if (assets == null) continue
      const date = iso(new Date(Number(y), 0, 1 + (r - 11)))
      if (date.slice(0, 4) !== y) continue
      const snap = { assets: Math.round(assets), debt: Math.round(debt ?? 0) }
      const note = val(ws, `F${r}`)
      if (typeof note === 'string' && note.trim()) snap.note = note.trim()
      data.snapshots[date] = snap
      const f = cell(ws, `D${r}`)?.f
      const m = f && f.match(/^D\d+\*([\d.]+)$/)
      if (m) loanFactor = Number(m[1])
      if (debt != null) lastLoan = { date, debt }
    }
  }

  // ---- Accounts: category rows × institution columns of the latest sheet.
  const latest = wb.Sheets[years.at(-1)]
  const CATS = { 3: 'investments', 4: 'crypto', 5: 'deposit', 6: 'cash' }
  const CAT_LABEL = { investments: 'Investments', crypto: 'Crypto', deposit: 'Deposit', cash: 'Cash' }
  let order = 0
  for (const col of ['D', 'E', 'F']) {
    const institution = String(val(latest, `${col}2`) || '').trim()
    if (!institution) continue
    for (const [row, category] of Object.entries(CATS)) {
      const balance = num(val(latest, `${col}${row}`))
      if (balance == null) continue
      data.accounts[key(`${institution}-${category}`)] = {
        name: `${institution} – ${CAT_LABEL[category]}`, institution, category, source: 'manual', balance, order: order++, updatedAt: years.at(-1) + '-01-01',
      }
    }
  }

  // ---- Loan: last known balance, compounding with the spreadsheet's daily factor.
  if (lastLoan) {
    data.loans.main = { name: 'Loan', balance: lastLoan.debt, asOf: lastLoan.date, dailyRate: loanFactor ? Number((loanFactor - 1).toFixed(10)) : 0 }
  }

  // ---- Recurring: monthly salary / repayment (row 8) and bill due days ("Hónap napja" block).
  const repayment = num(val(latest, 'E8'))
  const salary = num(val(latest, 'F8'))
  if (salary) data.recurring.salary = { name: 'Salary', type: 'income', kind: 'income', category: 'Salary', amount: Math.abs(salary), dayOfMonth: 10 }
  if (repayment) data.recurring['loan-repayment'] = { name: 'Loan repayment', type: 'expense', kind: 'bill', category: 'Loan', amount: Math.abs(repayment), dayOfMonth: 15 }
  const BILL_NAMES = { 'Telefon': ['Phone', 'Utilities'], 'Közös ktg.': ['Common costs', 'Housing'], 'Rezsi': ['Utilities', 'Utilities'], 'Netflix': ['Netflix', 'Subscriptions'] }
  const range = XLSX.utils.decode_range(latest['!ref'])
  let dueHeader = null
  for (let r = 1; r <= range.e.r + 1; r++) if (val(latest, `L${r}`) === 'Hónap napja') dueHeader = r
  if (dueHeader) {
    for (let r = dueHeader + 1; r <= dueHeader + 20; r++) {
      const name = val(latest, `K${r}`), day = num(val(latest, `L${r}`))
      if (typeof name !== 'string' || day == null) continue
      const [en, category] = BILL_NAMES[name.trim()] || [name.trim(), 'Other']
      data.recurring[key(en)] = { name: en, type: 'expense', kind: category === 'Subscriptions' ? 'subscription' : 'bill', category, amount: null, dayOfMonth: day }
    }
  }

  // ---- Planned purchases ("Tervezett vételek", columns V/W) from any EUR-era sheet; prices there are old HUF values.
  for (const y of years) {
    const ws = wb.Sheets[y]
    for (let r = 3; r <= 40; r++) {
      const name = val(ws, `V${r}`), price = num(val(ws, `W${r}`))
      if (typeof name !== 'string' || name.trim().length < 2 || /^\d/.test(name)) continue
      const k = key(name)
      if (data.wishlist[k]) continue
      data.wishlist[k] = { name: name.trim(), price: null, note: price ? `Old spreadsheet price: ${price.toLocaleString('hu-HU')} HUF` : null, order: Object.keys(data.wishlist).length }
    }
  }
  return data
}
